// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
// import * as sdk from '@canton-network/dapp-sdk';
import { useOperatorParty } from '@hooks/queries/utility-operator/useOperatorParty';
import { useCallback, useState } from 'react';
import { Box, Typography } from '@mui/material';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import { useModuleParty } from '../../../contexts/ModuleContext';
import useRequestComplianceService from '../../../hooks/mutations/registry/onboarding/useRequestComplianceService';
import useMutate from '../../../hooks/mutations/useMutate';
import {
  ERR_CREATE_COMPLIANCE_SERVICE_REQUEST,
  SUC_CREATE_COMPLIANCE_SERVICE_REQUEST,
} from '../../../utils/strings';

type DialogProps = {
  open: boolean;
  onClose: () => void;
};

export const RequestComplianceServiceDialog = ({ open, onClose }: DialogProps) => {
  const [isLoading, setLoading] = useState(false);
  const [upgrader, setUpgrader] = useState<string>('');

  const operator = useOperatorParty()?.data;
  const party = useModuleParty();
  const requestComplianceService = useRequestComplianceService();
  const mutate = useMutate();

  const resetState = useCallback(() => {
    setLoading(false);
    setUpgrader('');
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!operator) return;
    setLoading(true);
    try {
      await mutate(
        requestComplianceService,
        { operator, compliance: party, upgrader },
        SUC_CREATE_COMPLIANCE_SERVICE_REQUEST,
        ERR_CREATE_COMPLIANCE_SERVICE_REQUEST,
      );
    } finally {
      handleClose();
      setLoading(false);
    }
  }, [operator, party, upgrader, handleClose, mutate, requestComplianceService]);

  const disableSubmit = !operator || !upgrader || isLoading;

  return (
    <FormDialog
      open={open}
      title="Request Compliance Service"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Request"
      isSubmitDisabled={disableSubmit}
    >
      <Typography variant="body2">
        This will initiate a compliance service request for party{' '}
        <strong
          style={{
            display: 'inline-block',
            marginBlock: '0.5rem',
            overflowWrap: 'anywhere',
          }}
        >
          {party}
        </strong>{' '}
        from operator{' '}
        <strong
          style={{ display: 'inline-block', marginBlock: '0.5rem', overflowWrap: 'anywhere' }}
        >
          {operator}
        </strong>
        .
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
        <TextInput
          enableTopLabel
          label="Upgrader Party ID"
          value={upgrader}
          setValue={setUpgrader}
        />
      </Box>
    </FormDialog>
  );
};

export default RequestComplianceServiceDialog;
